import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export const METHODS = { nakit: "Nakit", havale: "Havale/EFT", kart: "Kredi Kartı", cek: "Çek", senet: "Senet" };
export const METHOD_ACCOUNT = { nakit: "kasa", havale: "banka", kart: "pos" };
export const ACCOUNT_TYPES = { kasa: "Kasa", banka: "Banka", pos: "POS", kredi_karti: "Kredi Kartı" };
export const BANKS = ["Ziraat Bankası", "Garanti BBVA", "İş Bankası", "Yapı Kredi", "Akbank", "Halkbank", "VakıfBank", "QNB", "Denizbank", "TEB", "Diğer"];

export const INSTR = {
  portfoyde: ["Portföyde", "bg-blue-50 text-blue-700 border-blue-200"],
  bankada: ["Bankada", "bg-indigo-50 text-indigo-700 border-indigo-200"],
  tahsil_edildi: ["Tahsil Edildi", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  ciro_edildi: ["Ciro Edildi", "bg-violet-50 text-violet-700 border-violet-200"],
  insaata_verildi: ["İnşaata Verildi", "bg-orange-50 text-orange-800 border-orange-200"],
  personele_verildi: ["Personele Verildi", "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200"],
  iade: ["İade", "bg-amber-50 text-amber-800 border-amber-200"],
  karsiliksiz: ["Karşılıksız", "bg-rose-100 text-rose-800 border-rose-300"],
  iptal: ["İptal", "bg-slate-100 text-slate-500 border-slate-200"],
};
export const PROMISE = {
  bekliyor: ["Bekliyor", "bg-amber-50 text-amber-800 border-amber-200"],
  kismen: ["Kısmen Ödendi", "bg-sky-50 text-sky-800 border-sky-200"],
  odendi: ["Ödendi", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  gecikti: ["Gecikti", "bg-rose-600 text-white border-rose-600"],
  iptal: ["İptal", "bg-slate-100 text-slate-500 border-slate-200 line-through"],
};
export const PROMISE_OPEN = ["bekliyor", "kismen", "gecikti"];
export const ITEM_TYPES = { fatura: "Fatura", cek: "Çek", senet: "Senet", soz: "Ödeme Sözü" };

const Pill = ({ map, k, testid }) => {
  const [label, cls] = map[k] || [k, ""];
  return <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border whitespace-nowrap ${cls}`} data-testid={testid}>{label}</span>;
};
export const InstrBadge = ({ status, testid }) => <Pill map={INSTR} k={status} testid={testid} />;
export const PromiseBadge = ({ status, testid }) => <Pill map={PROMISE} k={status} testid={testid} />;
export const PayStatus = ({ status, testid }) => (
  <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${status === "iptal" ? "bg-slate-100 text-slate-500 border-slate-200 line-through" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid={testid}>
    {status === "iptal" ? "İptal" : "Aktif"}
  </span>
);

export const todayIso = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

export const daysLabel = (days) => (days === 0 ? "Bugün" : days < 0 ? `${-days} gün gecikti` : `${days} gün kaldı`);

export const sel = "h-9 px-2 rounded-md border border-slate-200 bg-white text-sm";

export const Stat = ({ label, value, hint, tone = "text-slate-900", testid, active, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
    className={`panel p-3 text-left transition-[border-color,box-shadow] duration-150 ${active ? "ring-2 ring-blue-600 border-blue-600" : ""} ${onClick ? "hover:border-blue-400" : "cursor-default"}`}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-1 font-mono text-lg font-semibold ${tone}`} data-testid={`${testid}-value`}>{value}</div>
    {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
  </button>
);

export const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1 ${className}`}><Label className="text-xs text-slate-600">{label}</Label>{children}</div>
);

export function useAccounts(enabled = true) {
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { if (enabled) api.get("/tahsilatlar/accounts").then(({ data }) => setAccounts(data)).catch(() => {}); }, [enabled]);
  return accounts;
}

// v1.28.0 hesap rengi: ~12 ayırt edilebilir renk + özel hex. Rozet: açık zemin + koyu metin (kontrast güvenli)
export const ACCOUNT_PALETTE = ["#dc2626", "#ea580c", "#d97706", "#65a30d", "#059669", "#0d9488", "#0891b2", "#2563eb", "#4f46e5", "#7c3aed", "#c026d3", "#475569"];
export const AccDot = ({ color, className = "", testid }) => (color ? <span className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ring-1 ring-black/10 ${className}`} style={{ background: color }} data-testid={testid} /> : null);
export const accColor = (list, id) => list?.find((a) => a.id === id)?.color;
export const AccountBadge = ({ name, color, archived, testid }) => (
  name ? <span className="inline-flex items-center gap-1.5 max-w-full px-1.5 py-0.5 rounded border text-[12px] text-slate-900 whitespace-nowrap"
    style={color ? { background: `${color}14`, borderColor: `${color}66` } : undefined} title={archived ? `${name} (arşivde)` : name} data-testid={testid}>
    <AccDot color={color} /><span className="truncate">{name}</span>{archived && <span className="text-[10px] text-slate-500">arşiv</span>}
  </span> : <span className="text-slate-400">—</span>
);
export const AccLabel = ({ text, color }) => <span className="inline-flex items-center gap-1.5">{text}<AccDot color={color} /></span>;
